import {
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_CARD_WITH_HISTORY_FOR_PERSON,
  RESET_CARDS,
  SET_SEARCH_CARD_FORM,
} from "../../redux/reducers/cardReducer";
import {
  formatInput,
  lookupDefaultFormatsValidator,
} from "../../util/validators";
import {
  CardTypeDTO,
  CarePrincipalSettings,
} from "../../generated/api";
import { GET_CAREPRINCIPAL_SETTINGS } from "../../redux/reducers/administrationReducer";
import {
  GET_PERSON_CONSENT,
  GET_PERSON_NAME,
  RESET_PERSON_NAME,
} from "../../redux/reducers/personReducer";
import { RnrAlert } from "../../util/RnrAlert";
import { useNavigate, useLocation } from "react-router";
import { RESET_URL_QUERY_STATE_PARAMS } from "../../redux/reducers/loginReducer";

export function SearchCardForm() {
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const FONT_SIZE = 8;
  const MIN_NAME_WIDTH = 200;
  const MAX_NAME_WIDTH = 500;

  const isSearchCard = location.pathname.includes("search-card");

  const cardTypes = useSelector((state: any) => state.cardTypes.cardTypes);
  const patterns = useSelector(
    (state: any) => state.settings.internalSettings?.rnrPatterns
  );
  const carePrincipalId = useSelector(
    (state: any) => state.session.selectedCarePrincipal?.id
  );
  const session = useSelector((state: any) => state.session);
  const personName = useSelector((state: any) => state.person.name);
  const loading = useSelector((state: any) => state.person.nameLoading);
  const cardConsent = useSelector((state: any) => state.person.cardConsent);
  const carePrincipalSettings: CarePrincipalSettings = useSelector(
    (store: any) => store.carePrincipal.carePrincipalSettings
  );
  const isSearchLoading = useSelector((state: any) => state.loadingState.loading);
  const personId = useSelector((state: any) => state?.card?.params?.personId);

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [nameFieldWidth, setNameFieldWidth] = useState(MIN_NAME_WIDTH);

  const formHeight = "56px";
  const [defaultCardType] = useState({
    name: "Öppen Sjukvård",
    id: "CARE_VISIT",
  });

  useEffect(() => {
    if (personName && personName.length * FONT_SIZE > MIN_NAME_WIDTH && personName.length * FONT_SIZE < MAX_NAME_WIDTH) {
      setNameFieldWidth(personName.length * FONT_SIZE);
    } else if(personName && personName.length * FONT_SIZE > MAX_NAME_WIDTH) {
      setNameFieldWidth(MAX_NAME_WIDTH);
    } else {
      setNameFieldWidth(MIN_NAME_WIDTH);
    }
  }, [personName])

  useEffect(() => {
    setValue("personId", personId);
  }, [personId])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true });
      setIsSubmitSuccessful(false);
      dispatch({ type: RESET_URL_QUERY_STATE_PARAMS });
    }
  }, [isSubmitSuccessful, reset]);

  useEffect(() => {
    if (carePrincipalId) {
      dispatch({
        type: GET_CAREPRINCIPAL_SETTINGS,
        carePrincipalId: carePrincipalId,
      });
    }
  }, [carePrincipalId, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      session?.urlQueryState?.ssn &&
      carePrincipalId &&
      checkIfCardTypeExists(session?.urlQueryState?.nature) &&
      !isSearchCard
    ) {
      onSubmit(control._formValues, null);
    }
  }, [
    control,
    carePrincipalId,
    session.urlQueryState.ssn,
    cardTypes,
    carePrincipalSettings,
  ]); //eslint-disable-line react-hooks/exhaustive-deps

  function checkIfCardTypeExists(cardTypeId: any) {
    return (
      cardTypes?.filter((cardType: CardTypeDTO) => cardType.id === cardTypeId)
        .length > 0
    );
  }

  const onSubmit = (values: any, event: any) => {
    event?.preventDefault();
    dispatch({ type: RESET_PERSON_NAME });
    const strippedId = formatInput(values.personId);
    const params = {
      personId: strippedId,
      cardTypeId: values.cardType,
      carePrincipalId: carePrincipalId,
    };
    const personId = params.personId;
    const cardTypeId = params.cardTypeId;
    dispatch({ type: RESET_CARDS });
    dispatch({ type: SET_SEARCH_CARD_FORM, params });
    dispatch({ type: GET_CARD_WITH_HISTORY_FOR_PERSON, params });
    if (isSearchCard) {
      navigate("/search-card");
    }
    if (carePrincipalSettings?.puPresentation) {
      dispatch({
        type: GET_PERSON_NAME,
        carePrincipalId: carePrincipalId,
        personId: strippedId,
      });
    }
    dispatch({
      type: GET_PERSON_CONSENT,
      carePrincipalId,
      cardTypeId,
      personId,
    });
    setIsSubmitSuccessful(true);
  };

  return (
    cardTypes && (
      <Box
        sx={{ marginBottom: "2vh" }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack sx={{}} direction="row" spacing={2}>
          <Stack direction="column">
            <Controller
              name="personId"
              control={control}
              defaultValue={session?.urlQueryState?.ssn}
              render={() => (
                <TextField
                  label={t("searchCard.form.personIdLabel") + " *"}
                  helperText={t("searchCard.form.personIdHelp")}
                  error={errors.personId ? true : false}
                  disabled={session?.urlQueryState?.embedded === "true"}
                  id="personId"
                  {...register("personId", {
                    required: true,
                    validate: {
                      check: (v) => lookupDefaultFormatsValidator(v, patterns),
                    },
                  })}
                />
              )}
            />
            {errors.personId && errors.personId.type === "required" && (
              <Alert severity="error">{t("errors.emptyField")}</Alert>
            )}
          </Stack>
          {loading ? <CircularProgress /> : null}
          {personName && !cardConsent?.optOut ? (
            <TextField
              id="outlined-name"
              InputLabelProps={{ shrink: true }}
              label={t("name")}
              value={personName}
              disabled={true}
              inputProps={{
                style: { width: `${nameFieldWidth}px` }
              }}
            ></TextField>
          ) : null}
          <Stack direction="column">
            <Controller
              name="cardType"
              control={control}
              render={({
                field: {
                  value = session?.urlQueryState?.nature
                    ? session?.urlQueryState?.nature
                    : defaultCardType.id,
                },
              }) => (
                <TextField
                  select
                  label={t("searchCard.form.cardTypeLabel") + " *"}
                  value={value}
                  sx={{ width: "22ch" }}
                  error={errors.cardType ? true : false}
                  {...register("cardType", { required: true })}
                >
                  {cardTypes ? (
                    cardTypes.map((cardType: any, i: number) => (
                      <MenuItem key={i} id={cardType.id} value={cardType.id}>
                        {cardType.name}
                      </MenuItem>
                    ))
                  ) : (
                    <p>N/A</p>
                  )}
                </TextField>
              )}
            />
            {errors.cardType && errors.cardType.type === "required" && (
              <Typography
                variant="body2"
                sx={{ color: "#DC143C" }}
                role="alert"
              >
                {t("errors.chooseItem")}
              </Typography>
            )}
          </Stack>
          <Button disabled={isSearchLoading} type="submit" variant="contained" sx={{ height: formHeight }}>
            {t("searchCard.form.submit")}
          </Button>
        </Stack>
        {errors.personId && errors.personId.type === "check" && <RnrAlert />}
      </Box>
    )
  );
}
